/**
 * History Slider Module
 */

export default function initHistorySlider() {
  const historySliders = document.querySelectorAll('.history-slider');
  if (!historySliders.length) return;

  historySliders.forEach(slider => {
    const navSwiper = slider.querySelector('.history-nav-swiper');
    const contentSwiper = slider.querySelector('.history-content-swiper');
    const navPrevBtn = slider.querySelector('[id$="-nav-prev"]');
    const navNextBtn = slider.querySelector('[id$="-nav-next"]');
    
    if (!navSwiper || !contentSwiper) return;

    // Check if we're on tablet down (max-width: 767px)
    const isTabletDown = window.innerWidth <= 767;
    let currentNavIndex = 0;
    let maxVisibleItems = 3.5;
    let navItems = [];

    // Initialize navigation swiper
    const historyNavSwiper = new window.Swiper(navSwiper, {
      slidesPerView: isTabletDown ? 'auto' : 'auto',
      spaceBetween: 0,
      freeMode: true,
      watchSlidesProgress: true,
      direction: 'horizontal',
      allowTouchMove: true,
    });

    // Initialize content swiper
    const historyContentSwiper = new window.Swiper(contentSwiper, {
      modules: [window.SwiperModules.Navigation],
      slidesPerView: 1,
      centeredSlides: true,
      centerInsufficientSlides: true,
      spaceBetween: 30,
      navigation: {
        nextEl: slider.querySelector('.history-arrow.history-next'),
        prevEl: slider.querySelector('.history-arrow.history-prev'),
      },
      thumbs: {
        swiper: historyNavSwiper,
      },
      on: {
        slideChange: function() {
          updateDotIndicators();
          if (isTabletDown) {
            updateNavPosition();
          }
        }
      }
    });

    // Create custom dot indicators
    const createDotIndicators = () => {
      const navSlides = slider.querySelectorAll('.history-nav-item');
      const dotsContainer = slider.querySelector('.history-nav-pagination');
      
      // Clear existing dots
      dotsContainer.innerHTML = '';
      
      // Create a dot for each slide
      navSlides.forEach((slide, index) => {
        const dot = document.createElement('div');
        dot.className = 'history-dot';
        dot.setAttribute('data-slide-index', index);
        dotsContainer.appendChild(dot);
        
        // Add click handler to dot
        dot.addEventListener('click', () => {
          historyContentSwiper.slideTo(index);
        });
      });
    };

    // Update dot indicators based on active slide
    const updateDotIndicators = () => {
      const dots = slider.querySelectorAll('.history-dot');
      const activeIndex = historyContentSwiper.activeIndex;
      
      dots.forEach((dot, index) => {
        if (index === activeIndex) {
          dot.classList.add('active');
        } else {
          dot.classList.remove('active');
        }
      });
    };

    // Tablet down navigation functions
    const updateNavPosition = () => {
      if (!isTabletDown) return;
      
      const activeIndex = historyContentSwiper.activeIndex;
      const totalItems = navItems.length;
      
      // Calculate the optimal starting position to show the active item
      // and up to 3.5 items total
      let startIndex = Math.max(0, Math.min(activeIndex - 1, totalItems - Math.ceil(maxVisibleItems)));
      
      // Update the transform to slide the navigation
      const slideWidth = navItems[0]?.offsetWidth || 0;
      const gap = 24; // $spacing-md in pixels
      const offset = startIndex * (slideWidth + gap);
      
      navSwiper.querySelector('.swiper-wrapper').style.transform = `translateX(-${offset}px)`;
      
      // Update arrow states
      updateArrowStates(startIndex, totalItems);
    };

    const updateArrowStates = (startIndex, totalItems) => {
      if (!navPrevBtn || !navNextBtn) return;
      
      // Disable prev arrow if we're at the beginning
      if (startIndex === 0) {
        navPrevBtn.classList.add('disabled');
      } else {
        navPrevBtn.classList.remove('disabled');
      }
      
      // Disable next arrow if we can't show more items
      const maxStartIndex = Math.max(0, totalItems - Math.ceil(maxVisibleItems));
      if (startIndex >= maxStartIndex) {
        navNextBtn.classList.add('disabled');
      } else {
        navNextBtn.classList.remove('disabled');
      }
    };

    const navigateNav = (direction) => {
      const activeIndex = historyContentSwiper.activeIndex;
      const totalItems = navItems.length;
      
      let newIndex;
      if (direction === 'prev') {
        newIndex = Math.max(0, activeIndex - 1);
      } else {
        newIndex = Math.min(totalItems - 1, activeIndex + 1);
      }
      
      historyContentSwiper.slideTo(newIndex);
    };

    // Initialize tablet down navigation
    if (isTabletDown) {
      navItems = slider.querySelectorAll('.history-nav-item');
      
      // Add click handlers to navigation arrows
      if (navPrevBtn) {
        navPrevBtn.addEventListener('click', () => navigateNav('prev'));
      }
      
      if (navNextBtn) {
        navNextBtn.addEventListener('click', () => navigateNav('next'));
      }
      
      // Initial position update
      setTimeout(() => updateNavPosition(), 100);
    }

    // Handle window resize
    const handleResize = () => {
      const wasTabletDown = isTabletDown;
      const nowTabletDown = window.innerWidth <= 767;
      
      if (wasTabletDown !== nowTabletDown) {
        // Reinitialize if breakpoint changed
        location.reload(); // Simple reload for now, could be improved
      }
    };

    window.addEventListener('resize', handleResize);

    // Initialize dots
    createDotIndicators();
    updateDotIndicators();

    // Add click handlers to navigation items
    const navItemsForClick = slider.querySelectorAll('.history-nav-item');
    navItemsForClick.forEach((item, index) => {
      item.addEventListener('click', () => {
        historyContentSwiper.slideTo(index);
      });
    });
  });
}

// Auto-initialize
document.addEventListener('DOMContentLoaded', initHistorySlider);
